import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
export function HomePage() {
  usePageMeta('OnlineCollegeGuide | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. 100% free service.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Update URL if not already matching
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram)) {
      navigate(`/degree/${areaStudyStr}/${mProgram}`, {
        replace: true
      });
    }
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program]);
  // On mount, if params exist, search
  useEffect(() => {
    if (areaStudy && program) {
      fetchResults('', areaStudy, program);
    }
  }, [areaStudy, program, fetchResults]);
  return <main className="flex-grow bg-light" data-id="element-894">
      <HeroSection onSelectionChange={fetchResults} data-id="element-895" />
      <TrustSignals data-id="element-896" />

      {hasSearched ? <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-897">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-898">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-899">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-900">
                  <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-901" />
                  <p className="text-gray-600 font-medium" data-id="element-902">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-903">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-904" />
                  <div data-id="element-905">
                    <p className="text-red-800 font-medium" data-id="element-906">{apiError}</p>
                    <p className="text-red-600 text-sm mt-1" data-id="element-907">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-908">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-909">
                      <MatchedSchoolCard school={school} index={idx} data-id="element-910" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-911" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-912">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-913">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-914" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-915">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-916">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-917">
              <HomeInlineLeadCapture data-id="element-918" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-919" />}
        </div> : <>
          <DegreeCategoryGrid data-id="element-920" />
          <SEOContentSection data-id="element-921" />
          <FAQSection data-id="element-922" />
        </>}
    </main>;
}